import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { FileText, Loader2, Plus, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : '/api';

export default function AddCase() {
  const [autoNumber, setAutoNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('auto'); // 'auto' or 'manual'
  const navigate = useNavigate();

  const [manualForm, setManualForm] = useState({
    auto_number: '',
    case_number: '',
    year: new Date().getFullYear().toString(),
    case_type: 'مدني',
    court: '',
    department: '',
    status: 'منظورة',
    parties: [
      { name: '', role: 'مدعي' },
      { name: '', role: 'مدعى عليه' }
    ],
    next_hearing_date: '',
    next_hearing_notes: ''
  });

  const handleAutoSubmit = async (e) => {
    e.preventDefault();
    
    // Validate auto number - must be 9 digits
    const cleanNumber = autoNumber.trim().replace(/\D/g, '');
    if (cleanNumber.length !== 9) {
      toast.error('الرقم الآلي يجب أن يتكون من 9 أرقام');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API}/cases`, { auto_number: cleanNumber });
      toast.success('تم إضافة القضية بنجاح');
      navigate(`/cases/${response.data.id}`);
    } catch (error) {
      toast.error('فشل إضافة القضية');
    } finally {
      setLoading(false);
    }
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    
    // Validate auto number - must be 9 digits
    const cleanNumber = manualForm.auto_number.trim().replace(/\D/g, '');
    if (cleanNumber.length !== 9) {
      toast.error('الرقم الآلي يجب أن يتكون من 9 أرقام');
      return;
    }

    try {
      setLoading(true);
      
      // First create with auto number
      const caseData = {
        auto_number: cleanNumber,
        case_number: manualForm.case_number || null,
        year: manualForm.year,
        case_type: manualForm.case_type,
        court: manualForm.court,
        department: manualForm.department,
        status: manualForm.status,
        parties: manualForm.parties.filter(p => p.name && p.role),
      };

      if (manualForm.next_hearing_date) {
        caseData.next_hearing = {
          date: new Date(manualForm.next_hearing_date).toISOString(),
          notes: manualForm.next_hearing_notes
        };
      }

      const response = await axios.post(`${API}/cases`, { auto_number: cleanNumber });
      
      // Then update with all manual data
      await axios.put(`${API}/cases/${response.data.id}`, caseData);
      
      toast.success('تم إضافة القضية بنجاح');
      navigate(`/cases/${response.data.id}`);
    } catch (error) {
      toast.error('فشل إضافة القضية');
    } finally {
      setLoading(false);
    }
  };

  const addParty = () => {
    setManualForm({
      ...manualForm,
      parties: [...manualForm.parties, { name: '', role: '' }]
    });
  };

  const removeParty = (index) => {
    const newParties = manualForm.parties.filter((_, i) => i !== index);
    setManualForm({ ...manualForm, parties: newParties });
  };

  const updateParty = (index, field, value) => {
    const newParties = [...manualForm.parties];
    newParties[index][field] = value;
    setManualForm({ ...manualForm, parties: newParties });
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">إضافة قضية جديدة</h1>
        <p className="text-gray-500 mt-2">اختر طريقة إضافة القضية</p>
      </div>

      <Tabs defaultValue="auto" onValueChange={(v) => setMode(v)}>
        <TabsList className="grid w-full grid-cols-2 mb-6">
          <TabsTrigger value="auto">جلب تلقائي</TabsTrigger>
          <TabsTrigger value="manual">إدخال يدوي</TabsTrigger>
        </TabsList>

        <TabsContent value="auto">
          <Card data-testid="add-case-form">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <CardTitle>جلب البيانات تلقائياً</CardTitle>
                  <CardDescription>سيتم جلب البيانات تلقائياً من موقع وزارة العدل</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAutoSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="auto-number" className="text-base font-medium">
                    الرقم الآلي *
                  </Label>
                  <Input
                    id="auto-number"
                    data-testid="auto-number-input"
                    type="text"
                    placeholder="أدخل 9 أرقام مثال: 254696500"
                    value={autoNumber}
                    onChange={(e) => setAutoNumber(e.target.value.replace(/\D/g, '').slice(0, 9))}
                    className="text-lg h-12"
                    disabled={loading}
                    maxLength={9}
                  />
                  <p className="text-sm text-gray-500">
                    الرقم الآلي يتكون من 9 أرقام - يمكنك إيجاده في صفحة الاستعلام عن القضايا
                  </p>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 mb-2">ماذا سيحدث بعد الإضافة؟</h4>
                  <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
                    <li>جلب تفاصيل القضية من موقع وزارة العدل</li>
                    <li>إنشاء حدث تلقائي في Google Calendar للجلسة القادمة</li>
                    <li>إرسال إشعار عبر Telegram Bot</li>
                    <li>بدء المتابعة التلقائية للتحديثات</li>
                  </ul>
                </div>

                <div className="flex gap-3">
                  <Button
                    data-testid="submit-case-btn"
                    type="submit"
                    className="flex-1 h-12 text-base bg-blue-600 hover:bg-blue-700"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 ml-2 animate-spin" />
                        جاري الإضافة...
                      </>
                    ) : (
                      'إضافة القضية'
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/')}
                    className="h-12"
                    disabled={loading}
                  >
                    إلغاء
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="manual">
          <form onSubmit={handleManualSubmit}>
            <Card>
              <CardHeader>
                <CardTitle>المعلومات الأساسية</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="m_auto_number">الرقم الآلي * (9 أرقام)</Label>
                    <Input
                      id="m_auto_number"
                      value={manualForm.auto_number}
                      onChange={(e) => setManualForm({ ...manualForm, auto_number: e.target.value.replace(/\D/g, '').slice(0, 9) })}
                      placeholder="254696500"
                      maxLength={9}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="m_case_number">رقم القضية (اختياري)</Label>
                    <Input
                      id="m_case_number"
                      value={manualForm.case_number}
                      onChange={(e) => setManualForm({ ...manualForm, case_number: e.target.value })}
                      placeholder="7003"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="m_year">السنة</Label>
                    <Input
                      id="m_year"
                      value={manualForm.year}
                      onChange={(e) => setManualForm({ ...manualForm, year: e.target.value })}
                      placeholder="2025"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="m_case_type">نوع القضية</Label>
                    <select
                      id="m_case_type"
                      value={manualForm.case_type}
                      onChange={(e) => setManualForm({ ...manualForm, case_type: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="مدني">مدني</option>
                      <option value="تجاري">تجاري</option>
                      <option value="جنائي">جنائي</option>
                      <option value="أحوال شخصية">أحوال شخصية</option>
                      <option value="عمالي">عمالي</option>
                      <option value="إداري">إداري</option>
                    </select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="m_court">المحكمة</Label>
                    <Input
                      id="m_court"
                      value={manualForm.court}
                      onChange={(e) => setManualForm({ ...manualForm, court: e.target.value })}
                      placeholder="محكمة الكويت الكلية"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="m_department">الدائرة</Label>
                    <Input
                      id="m_department"
                      value={manualForm.department}
                      onChange={(e) => setManualForm({ ...manualForm, department: e.target.value })}
                      placeholder="الدائرة الأولى"
                    />
                  </div>
                  
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="m_status">الحالة *</Label>
                    <select
                      id="m_status"
                      value={manualForm.status}
                      onChange={(e) => setManualForm({ ...manualForm, status: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="منظورة">منظورة</option>
                      <option value="مؤجلة">مؤجلة</option>
                      <option value="محجوزة للحكم">محجوزة للحكم</option>
                      <option value="شطب">شطب</option>
                      <option value="صدر حكم">صدر حكم</option>
                    </select>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="mt-6">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>أطراف القضية</CardTitle>
                  <Button type="button" onClick={addParty} size="sm" variant="outline">
                    <Plus className="w-4 h-4 ml-1" />
                    إضافة طرف
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {manualForm.parties.map((party, index) => (
                  <div key={index} className="flex gap-3 items-start">
                    <div className="flex-1 grid grid-cols-2 gap-3">
                      <Input
                        placeholder="الاسم"
                        value={party.name}
                        onChange={(e) => updateParty(index, 'name', e.target.value)}
                      />
                      <Input
                        placeholder="الصفة (مدعي، مدعى عليه...)"
                        value={party.role}
                        onChange={(e) => updateParty(index, 'role', e.target.value)}
                      />
                    </div>
                    {manualForm.parties.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeParty(index)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="mt-6">
              <CardHeader>
                <CardTitle>الجلسة القادمة (اختياري)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="m_hearing_date">تاريخ ووقت الجلسة</Label>
                  <Input
                    id="m_hearing_date"
                    type="datetime-local"
                    value={manualForm.next_hearing_date}
                    onChange={(e) => setManualForm({ ...manualForm, next_hearing_date: e.target.value })}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="m_hearing_notes">ملاحظات الجلسة</Label>
                  <Textarea
                    id="m_hearing_notes"
                    value={manualForm.next_hearing_notes}
                    onChange={(e) => setManualForm({ ...manualForm, next_hearing_notes: e.target.value })}
                    placeholder="جلسة مرافعة، لاستكمال البينة..."
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>

            <div className="flex gap-3 mt-6">
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 h-12 text-base bg-blue-600 hover:bg-blue-700"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 ml-2 animate-spin" />
                    جاري الإضافة...
                  </>
                ) : (
                  'إضافة القضية'
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/')}
                className="h-12"
                disabled={loading}
              >
                إلغاء
              </Button>
            </div>
          </form>
        </TabsContent>
      </Tabs>
    </div>
  );
}